import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mileage, WITHHELD_BECAUSE, withheldSentence } from '@cwos/api-client';
import {
  ErrorPanel,
  Pill,
  SkeletonSet,
  SuccessEmpty,
  TableCard,
  useTabListKeys,
  type Column,
} from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { ListState } from '../ListStates.js';
import { listPhase } from '../listState.js';
import {
  amountLabel,
  awaitingClassificationNote,
  EXPORT_HAS_NO_FILE_YET,
  mileageExportKey,
  mileageExportOutcome,
  mileageExportRunsFor,
  readingsLabel,
  statusPill,
  tripsFor,
  type MileageTab,
} from './mileageLogic.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Mileage, the reviewer's screen.
 *
 * The screen a supervisor uses to approve travel reimbursement, built around a
 * constraint most mileage products ignore: an employer is not entitled to see
 * where their staff went on their own time.
 *
 * So this screen shows ONE thing. Every row is a drive the worker classified as
 * WORK, shown in full -- route, capture quality, the schedule context it was
 * matched against -- because they put it forward for payment and it has to be
 * reviewable.
 *
 * A personal drive is not here. Not as a redacted row, not as a date and a
 * distance, not in a total. It never leaves the server for this surface. That
 * is not a filter this screen applies; asking the API for everything returns the
 * same work-only list, so there is nothing here to reveal even by accident.
 *
 * What IS reported is how much driving nobody has answered for yet, as a count
 * and a distance. An unclassified drive is not private, it is unanswered, and a
 * reviewer needs to be able to chase the people who owe them an answer.
 *
 * There is no "Not yours to see" tab. It grouped personal drives together with
 * unclassified ones by the least useful property they share, and offered a list
 * of rows nobody could act on. A drive that is settled needs no tab; a drive
 * that is unanswered belongs in the sentence above, and in mileage exceptions
 * where work that needs a person is supposed to live.
 */
const TABS: Array<{ key: MileageTab; label: string }> = [
  { key: 'review', label: 'Awaiting review' },
  { key: 'work', label: 'Work' },
  { key: 'all', label: 'Everything' },
];

export function Mileage() {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canApprove = has('mileage:approve');
  const canExport = has('mileage:export');

  const [tab, setTab] = useState<MileageTab>('review');
  const viewTabs = useTabListKeys<HTMLDivElement>();
  const [selected, setSelected] = useState<string | null>(null);
  const [returnReason, setReturnReason] = useState('');
  const [reimburseReason, setReimburseReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [exportStart, setExportStart] = useState('');
  const [exportEnd, setExportEnd] = useState('');
  const [exportError, setExportError] = useState<string | null>(null);
  /**
   * WHAT THE LAST RUN ACTUALLY DID. `POST /mileage/exports` answers `{ export,
   * created }` and this screen used to discard `created`, so a re-run that
   * assembled nothing refreshed the history, showed a row, and let the operator
   * read it as the export they had just made.
   */
  const [exportOutcome, setExportOutcome] = useState<{
    created: boolean;
    message: string;
  } | null>(null);

  const list = useQuery({
    queryKey: ['mileage', 'trips'],
    queryFn: () => mileage.trips({ limit: 200 }),
  });

  // Mileage that is stuck: unanswered drives, unapproved claims, claims built
  // from patchy capture. Read-only and coordinate-free, so it can be shown
  // beside the list without widening what a reviewer may see.
  const stuck = useQuery({
    queryKey: ['mileage', 'exceptions'],
    queryFn: () => mileage.exceptions(),
  });

  const rows = useMemo(() => tripsFor(list.data?.trips ?? [], tab), [list.data, tab]);
  const selectedTrip = rows.find((t) => t.id === selected) ?? null;

  const approve = useMutation({
    mutationFn: (id: string) => mileage.approve(id),
    onSuccess: () => {
      setActionError(null);
      void qc.invalidateQueries({ queryKey: ['mileage', 'trips'] });
    },
    onError: (err: unknown) => setActionError(messageOf(err)),
  });

  const sendBack = useMutation({
    mutationFn: (id: string) => mileage.returnForCorrection(id, { reason: returnReason.trim() }),
    onSuccess: () => {
      setActionError(null);
      setReturnReason('');
      void qc.invalidateQueries({ queryKey: ['mileage', 'trips'] });
    },
    onError: (err: unknown) => setActionError(messageOf(err)),
  });

  const markReimbursed = useMutation({
    mutationFn: (id: string) => mileage.reimburse(id, { reason: reimburseReason.trim() }),
    onSuccess: () => {
      setActionError(null);
      setReimburseReason('');
      void qc.invalidateQueries({ queryKey: ['mileage', 'trips'] });
    },
    onError: (err: unknown) => setActionError(messageOf(err)),
  });

  // Payroll export — assembles every approved-and-unexported drive in a pay
  // period into one run. Read-only for anyone without mileage:export; the
  // query is still safe to fetch (the server enforces the permission), but it
  // is only requested when the caller can plausibly use it.
  const exports = useQuery({
    queryKey: ['mileage', 'exports'],
    queryFn: () => mileage.listExports(),
    enabled: canExport,
  });

  /**
   * How many runs this period already has, or `undefined` when the history
   * could not be read. It decides the idempotency key AND whether the card may
   * say anything about what has already been run — an unread history is not an
   * unexported period.
   */
  const priorRuns = mileageExportRunsFor(exports.data?.exports, exportStart, exportEnd);

  const runExport = useMutation({
    mutationFn: () =>
      mileage.createExport({
        payPeriodStart: exportStart,
        payPeriodEnd: exportEnd,
        // Derived from the runs the SERVER holds, not from a fixed string. See
        // `mileageExportKey`: the old key made a second run for a period
        // impossible, which meant a drive corrected and re-approved after the
        // period was exported could never reach payroll at all.
        idempotencyKey: mileageExportKey(exportStart, exportEnd, priorRuns),
      }),
    onSuccess: (result) => {
      setExportError(null);
      setExportOutcome(mileageExportOutcome(result));
      void qc.invalidateQueries({ queryKey: ['mileage', 'exports'] });
      void qc.invalidateQueries({ queryKey: ['mileage', 'trips'] });
    },
    onError: (err: unknown) => {
      setExportOutcome(null);
      setExportError(messageOf(err));
    },
  });

  const columns: Column<mileage.AdminTrip>[] = [
    {
      key: 'when',
      header: 'When',
      render: (r) => (
        <span>
          {new Date(r.startedAt).toLocaleDateString(undefined, {
            day: 'numeric',
            month: 'short',
          })}{' '}
          <span className="rn-muted">
            {new Date(r.startedAt).toLocaleTimeString(undefined, {
              hour: 'numeric',
              minute: '2-digit',
            })}
          </span>
        </span>
      ),
    },
    {
      key: 'worker',
      header: 'Worker',
      // A name, not eight characters of a UUID. Reviewing someone's travel
      // claim without being told whose it is asks the reviewer to do the
      // lookup the product should have done.
      // "Worker no longer on file" asserts that the account is GONE — the one
      // claim the withholding contract calls out as the distinction that
      // matters most, because it is exactly what a reader without `user:read`
      // must not be told. `AdminTrip.workerDisplayName` is a bare `string |
      // null` with no sibling flag (notation A, minus the flag), so the client
      // cannot tell a deleted worker from a withheld name — and when it cannot
      // tell, it must not pick. This says what is true either way.
      render: (r) => (
        <span>
          {r.workerDisplayName ?? <span className="rn-muted">No name on this claim</span>}
        </span>
      ),
    },
    { key: 'miles', header: 'Miles', numeric: true, render: (r) => r.miles.toFixed(1) },
    {
      key: 'what',
      header: 'What it was',
      render: (r) => {
        const pill = statusPill(r);
        return (
          <Pill variant={pill.variant} dot>
            {pill.label}
          </Pill>
        );
      },
    },
    {
      key: 'detail',
      header: 'Detail',
      render: (r) =>
        r.detailRestricted ? (
          // Said plainly, not left as an empty cell. An empty cell reads as
          // missing data; this is withheld data, and the difference matters.
          <span className="rn-muted">{withheldSentence(WITHHELD_BECAUSE.personalTrip)}</span>
        ) : (
          <span>{r.description ?? readingsLabel(r)}</span>
        ),
    },
  ];

  return (
    <div className="rn-col">
      <div style={{ marginBottom: 'var(--space-16)' }}>
        <h1 className="rn-card-title">Mileage</h1>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          Drives your team marked as work, and what they are owed. A personal drive stays with the
          person who made it: it is not listed here, and not counted anywhere.
        </p>
      </div>

      {/* ids + a real panel + arrow keys — see SupervisionCenter. */}
      <div
        role="tablist"
        aria-label="Mileage views"
        style={{ display: 'flex', gap: 'var(--space-8)', marginBottom: 'var(--space-12)' }}
        {...viewTabs}
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`mileage-tab-${t.key}`}
            aria-controls="mileage-panel"
            aria-selected={tab === t.key}
            className="rn-tab-btn"
            onClick={() => {
              setTab(t.key);
              setSelected(null);
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div id="mileage-panel" role="tabpanel" aria-labelledby={`mileage-tab-${tab}`}>
        {/* Rendered only when there is genuinely something to say. The note is
          about what has been WITHHELD, so inventing one from an absent summary
          would be a claim about privacy that no data supports. */}
        {awaitingClassificationNote(list.data?.awaitingClassification) ? (
          <p className="rn-muted" style={{ marginBottom: 'var(--space-12)' }}>
            {awaitingClassificationNote(list.data?.awaitingClassification)}
          </p>
        ) : null}

        {actionError ? <ErrorPanel message={actionError} /> : null}

        {/* A "Needs attention" section that is absent when nothing needs
            attention is right. One that is absent because the request failed
            is not: the reader cannot tell those apart, and one of them means
            somebody's drive is stuck. */}
        {stuck.isError && stuck.data === undefined ? (
          <section className="rn-card" style={{ marginBottom: 'var(--space-16)' }}>
            <h2 className="rn-card-title">Needs attention</h2>
            <LoadFailure
              error={stuck.error}
              message="We couldn’t check for drives that are stuck. Treat this as unknown rather than as none."
              onRetry={() => void stuck.refetch()}
            />
          </section>
        ) : null}

        {(stuck.data ?? []).length > 0 ? (
          <section className="rn-card" style={{ marginBottom: 'var(--space-16)' }}>
            <h2 className="rn-card-title">Needs attention</h2>
            <ul style={{ marginTop: 'var(--space-8)', display: 'grid', gap: 'var(--space-10)' }}>
              {(stuck.data ?? []).map((item) => (
                <li key={item.dedupKey}>
                  {/* No class: unlike the Timesheets session link, this button
                      already carries the whole reset inline — and deliberately
                      keeps `color: inherit` so the `<strong>` title and the
                      `.rn-muted` description colour themselves, which is what
                      `.rn-link` would override. */}
                  <button
                    type="button"
                    onClick={() => {
                      setTab('all');
                      setSelected(item.tripId);
                    }}
                    style={{
                      background: 'none',
                      border: 0,
                      padding: 0,
                      textAlign: 'left',
                      cursor: 'pointer',
                      font: 'inherit',
                      color: 'inherit',
                    }}
                  >
                    <strong>{item.title}</strong>
                    <span className="rn-muted"> — {item.description}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {list.isLoading ? (
          <SkeletonSet surface="table" rows={6} />
        ) : list.isError && !list.data ? (
          <LoadFailure
            error={list.error}
            message="We couldn’t load mileage just now."
            onRetry={() => void list.refetch()}
          />
        ) : rows.length === 0 ? (
          <SuccessEmpty
            heading={tab === 'review' ? 'Nothing waiting on you' : 'Nothing here'}
            message={
              tab === 'review'
                ? 'No work mileage is awaiting your review.'
                : 'No drives match this view.'
            }
          />
        ) : (
          <TableCard
            columns={columns}
            rows={rows}
            rowKey={(r) => r.id}
            selectedId={selected ?? undefined}
            onRowOpen={(r) => setSelected(r.id)}
          />
        )}

        {selectedTrip ? (
          <section className="rn-card" style={{ marginTop: 'var(--space-16)' }}>
            <h2 className="rn-card-title">
              {selectedTrip.miles.toFixed(1)} miles ·{' '}
              {new Date(selectedTrip.startedAt).toLocaleString()}
            </h2>

            {selectedTrip.detailRestricted ? (
              <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
                This drive is the worker’s own. You can see that it happened and how far it was,
                because that is what mileage totals are made of — but not where they went, unless
                they mark it as work.
              </p>
            ) : (
              <>
                {selectedTrip.description ? (
                  <p style={{ marginTop: 'var(--space-8)' }}>{selectedTrip.description}</p>
                ) : null}

                <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
                  {selectedTrip.source === 'manual'
                    ? 'Entered by the worker. There is no captured route — this is their account of the drive.'
                    : `${readingsLabel(selectedTrip)}, captured along the way. ` +
                      'Distance is measured from those readings, not estimated from a map.'}
                </p>

                {/*
                  The withheld track, said out loud.

                  This screen never plotted the readings — it printed how many
                  there were — but the API used to send every one of them, so a
                  reviewer who opened the network tab held a colleague's
                  minute-by-minute whereabouts. It does not any more, and a
                  reviewer is told that rather than left to infer it from a
                  number with nothing behind it. `routeWithheld` is false for a
                  manual drive, where nothing is being kept back.
                */}
                {selectedTrip.routeWithheld ? (
                  <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
                    The readings themselves are not shown. Where somebody drove, minute by minute,
                    stays with them even on a drive they put forward for reimbursement — what you
                    need to judge the claim is below.
                  </p>
                ) : null}

                {(selectedTrip.qualityFlags ?? []).length > 0 ? (
                  <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
                    Capture notes: {(selectedTrip.qualityFlags ?? []).join(', ')}
                  </p>
                ) : null}

                {(selectedTrip.context ?? []).length > 0 ? (
                  <div style={{ marginTop: 'var(--space-12)' }}>
                    <h2 className="rn-card-title">What else was happening</h2>
                    <ul className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
                      {(selectedTrip.context ?? []).map((m, i) => (
                        <li key={`${m.kind}-${m.sourceId ?? i}`}>{contextLine(m)}</li>
                      ))}
                    </ul>
                    <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
                      Context is evidence about the drive, not a judgement on it. The worker decided
                      what it was.
                    </p>
                  </div>
                ) : null}
              </>
            )}

            {selectedTrip.eligibility === 'excluded' && selectedTrip.eligibilityReason ? (
              <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
                Not reimbursable: {selectedTrip.eligibilityReason}
              </p>
            ) : null}

            {canApprove && selectedTrip.reviewState === 'ready_for_review' ? (
              <div
                style={{ marginTop: 'var(--space-16)', display: 'grid', gap: 'var(--space-10)' }}
              >
                <div>
                  <button
                    type="button"
                    className="signin-devbtn"
                    style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                    disabled={approve.isPending}
                    onClick={() => approve.mutate(selectedTrip.id)}
                  >
                    {approve.isPending ? 'Approving…' : 'Approve for reimbursement'}
                  </button>
                </div>
                <label style={{ display: 'grid', gap: 'var(--space-6)' }}>
                  <span className="rn-muted">
                    Or send it back, with something the worker can act on
                  </span>
                  <input
                    style={{ marginBottom: 0 }}
                    aria-label="Reason for sending this drive back"
                    placeholder="e.g. this looks like your commute — please check"
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                  />
                </label>
                <div>
                  <button
                    type="button"
                    className="signin-devbtn"
                    style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                    disabled={sendBack.isPending || returnReason.trim() === ''}
                    onClick={() => sendBack.mutate(selectedTrip.id)}
                  >
                    {sendBack.isPending ? 'Sending…' : 'Send back'}
                  </button>
                </div>
                <p className="rn-muted">
                  You cannot approve your own mileage — Phibian refuses it regardless of your role.
                </p>
              </div>
            ) : null}

            {/* Phibian moves no money. This just records that an approved drive was
              paid outside Phibian, so it stops showing as owing. */}
            {canApprove && selectedTrip.reviewState === 'approved' ? (
              <div
                style={{ marginTop: 'var(--space-16)', display: 'grid', gap: 'var(--space-10)' }}
              >
                <label style={{ display: 'grid', gap: 'var(--space-6)' }}>
                  <span className="rn-muted">
                    Approved and owed. Once it has actually been paid — through payroll or otherwise
                    — record that here.
                  </span>
                  <input
                    style={{ marginBottom: 0 }}
                    aria-label="How this drive was paid"
                    placeholder="e.g. included in the 15th payroll run"
                    value={reimburseReason}
                    onChange={(e) => setReimburseReason(e.target.value)}
                  />
                </label>
                <div>
                  <button
                    type="button"
                    className="signin-devbtn"
                    style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                    disabled={markReimbursed.isPending || reimburseReason.trim() === ''}
                    onClick={() => markReimbursed.mutate(selectedTrip.id)}
                  >
                    {markReimbursed.isPending ? 'Saving…' : 'Mark paid'}
                  </button>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        {canExport ? (
          <section className="rn-card" style={{ marginTop: 'var(--space-16)' }}>
            <h2 className="rn-card-title">Payroll export</h2>
            <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
              Assemble every approved, unpaid drive in a pay period into one export. A drive that
              has already been exported is never swept again — the server selects only
              approved-and-unexported claims — so running a period a second time picks up what has
              been approved since and cannot pay anything twice.
            </p>
            {/* Said out loud rather than left to be discovered. An operator who
                has just marked a fortnight of driving as paid will go looking
                for something to hand payroll, and there is nothing to find. */}
            <p className="rn-muted" style={{ marginTop: 'var(--space-6)', fontSize: 13 }}>
              {EXPORT_HAS_NO_FILE_YET}
            </p>

            {exportError ? (
              <div style={{ marginTop: 'var(--space-8)' }}>
                <ErrorPanel message={exportError} />
              </div>
            ) : null}

            <div
              style={{
                marginTop: 'var(--space-12)',
                display: 'flex',
                gap: 'var(--space-10)',
                flexWrap: 'wrap',
                alignItems: 'end',
              }}
            >
              <label style={{ display: 'grid', gap: 'var(--space-6)' }}>
                <span className="rn-muted">Pay period start</span>
                <input
                  style={{ marginBottom: 0 }}
                  type="date"
                  aria-label="Pay period start"
                  value={exportStart}
                  onChange={(e) => setExportStart(e.target.value)}
                />
              </label>
              <label style={{ display: 'grid', gap: 'var(--space-6)' }}>
                <span className="rn-muted">Pay period end</span>
                <input
                  style={{ marginBottom: 0 }}
                  type="date"
                  aria-label="Pay period end"
                  value={exportEnd}
                  onChange={(e) => setExportEnd(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={runExport.isPending || exportStart === '' || exportEnd === ''}
                onClick={() => runExport.mutate()}
              >
                {runExport.isPending
                  ? 'Assembling…'
                  : priorRuns !== undefined && priorRuns > 0
                    ? 'Export drives approved since'
                    : 'Create export'}
              </button>
            </div>

            {/* Only from a history that ARRIVED. `priorRuns` is undefined when
                the read failed, and "this period has not been exported" is
                exactly the claim a failed read must not be allowed to make. */}
            {priorRuns !== undefined && priorRuns > 0 ? (
              <p
                className="rn-muted"
                role="status"
                style={{ marginTop: 'var(--space-8)', fontSize: 13 }}
              >
                {priorRuns === 1
                  ? 'This period has been exported once already.'
                  : `This period has been exported ${priorRuns} times already.`}{' '}
                Running it again assembles only the drives approved since the last run; if there are
                none, the export is refused rather than written empty.
              </p>
            ) : null}

            {exportOutcome ? (
              <p
                role="status"
                className={exportOutcome.created ? undefined : 'rn-muted'}
                style={{ marginTop: 'var(--space-8)', fontSize: 13 }}
              >
                {exportOutcome.message}
              </p>
            ) : null}

            {/* "No payroll export has been run yet" was rendered from
                `exports.data?.exports ?? []`, so a failed read told a payroll
                administrator that nobody had run one — which is exactly the
                fact they came here to check before running another. */}
            <div style={{ marginTop: 'var(--space-16)' }}>
              <ListState
                phase={listPhase(exports, (exports.data?.exports ?? []).length)}
                rows={3}
                onRetry={() => void exports.refetch()}
                failedMessage="We couldn’t load the export history just now, so we cannot say whether one has already been run."
                empty={
                  <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
                    No payroll export has been run yet.
                  </p>
                }
              >
                <TableCard
                  columns={exportColumns}
                  rows={exports.data?.exports ?? []}
                  rowKey={(r) => r.id}
                />
              </ListState>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}

const exportColumns: Column<mileage.MileageExport>[] = [
  {
    key: 'period',
    header: 'Pay period',
    render: (e) => `${e.payPeriodStart} – ${e.payPeriodEnd}`,
  },
  { key: 'trips', header: 'Drives', numeric: true, render: (e) => String(e.tripCount ?? '—') },
  { key: 'miles', header: 'Miles', numeric: true, render: (e) => e.totalMiles.toFixed(1) },
  { key: 'amount', header: 'Amount', numeric: true, render: (e) => amountLabel(e.totalCents) },
  {
    key: 'status',
    header: 'Status',
    render: (e) => (
      <Pill variant="good" dot>
        {e.status}
      </Pill>
    ),
  },
];

/** A context match, as one readable line. */
function contextLine(match: mileage.MileageContextMatch): string {
  const d = match.detail;
  if (match.kind === 'scheduled_shift') {
    return d.programName ? `Rostered on ${String(d.programName)}` : 'A scheduled shift';
  }
  if (match.kind === 'time_session') return 'On the clock at the time';
  if (match.kind === 'scheduled_service') {
    const what = d.serviceTypeName ? String(d.serviceTypeName) : 'A visit';
    const client = d.clientReference ? ` (${String(d.clientReference)})` : '';
    return `${what}${client} nearby in time`;
  }
  if (match.kind === 'work_site') {
    return d.end === 'origin' ? `Started at ${String(d.name)}` : `Ended at ${String(d.name)}`;
  }
  if (match.kind === 'program') return `Programme: ${String(d.programName ?? 'unnamed')}`;
  return match.kind;
}

/**
 * The server's message when it has one.
 *
 * Mileage refusals are specific and actionable — "you cannot approve your own
 * mileage", "no rate was in force on the day of this drive" — so passing them
 * through beats a generic banner.
 */
function messageOf(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return 'That did not go through.';
}
